import React from 'react';
import type { FormValue, Question, OASISFormData } from '../types';
import type { LayoutNode } from './layout/types';
import { BespokeLayoutRenderer } from './layout/BespokeLayoutRenderer';

export interface SchemaQuestionOption {
  qid: string;
  label: string;
  category: string;
}

/**
 * Props that every bespoke form section component receives.
 */
export interface BespokeFormSectionProps {
  /** Current answers keyed by QID */
  answers: Record<string, FormValue>;
  /** Update a single answer by QID */
  onAnswerChange: (qid: string, value: FormValue) => void;
  /** Batch-update multiple answers */
  onFormDataChange: (updates: Partial<OASISFormData>) => void;
  /** The section's question schema for reference */
  schema: Question[];
  /** Flat list of all QIDs across all sections (for lookups / bespoke UIs) */
  schemaQuestionOptions: SchemaQuestionOption[];
  /** The section/category name */
  sectionName: string;
}

export type BespokeSectionComponent =
  React.ComponentType<BespokeFormSectionProps>;

// ---------------------------------------------------------------------------
// DB-backed bespoke config (JSON layout per section).
// The API may still return a deprecated `component_name` column — it is ignored.
// ---------------------------------------------------------------------------

export interface BespokeConfigEntry {
  section_name: string;
  layout_json: LayoutNode[] | null;
}

/** Map shape held in React state: schemaVersionName → sectionName → entry */
export type BespokeDbConfig = ReadonlyMap<
  string,
  ReadonlyMap<string, BespokeConfigEntry>
>;

/**
 * Stable component cache keyed by layout array reference.
 *
 * `resolveBespokeSection` is called during render; without this cache every
 * call would produce a new function identity, causing React to unmount and
 * remount the bespoke section on every parent re-render.  A WeakMap is used
 * so cached entries are GC'd when the layout array itself is no longer held
 * (e.g. after a config reload replaces `bespokeDbConfig`).
 */
const _jsonComponentCache = new WeakMap<
  LayoutNode[],
  BespokeSectionComponent
>();

function _getOrCreateJsonComponent(
  layout: LayoutNode[]
): BespokeSectionComponent {
  let comp = _jsonComponentCache.get(layout);
  if (!comp) {
    function BespokeJsonSection(props: BespokeFormSectionProps) {
      return React.createElement(BespokeLayoutRenderer, { ...props, layout });
    }
    _jsonComponentCache.set(layout, BespokeJsonSection);
    comp = BespokeJsonSection;
  }
  return comp;
}

/**
 * `dbConfig` maps schema version name → section name → BespokeConfigEntry.
 * Pass the map held in React state for the form instance.
 */
export function resolveBespokeSection(
  dbConfig: BespokeDbConfig | null | undefined,
  schemaVersionName: string | undefined,
  sectionName: string
): BespokeSectionComponent | null {
  if (!schemaVersionName) return null;

  const entry = dbConfig?.get(schemaVersionName)?.get(sectionName);

  if (entry?.layout_json) {
    return _getOrCreateJsonComponent(entry.layout_json);
  }

  return null;
}

export function hasBespokeSection(
  dbConfig: BespokeDbConfig | null | undefined,
  schemaVersionName: string | undefined,
  sectionName: string
): boolean {
  return (
    resolveBespokeSection(dbConfig, schemaVersionName, sectionName) !== null
  );
}
